import { lazy, Suspense } from 'react'
import { Navigate, Outlet, useRoutes } from 'react-router-dom'
import { PAGE_URL } from 'core/constants/path'
import { AuthFormProvider } from 'core/contexts/AuthFormContext'
import RedirectValidationGate from '@components/auth/RedirectValidationGate/RedirectValidationGate'
import ProtectedRoute from './Protected'
import { AuthLayout } from 'layouts/AuthLayout/AuthLayout'

const Auth = lazy(() => import('@pages/auth/Auth'))
const TenantSelection = lazy(
  () => import('@pages/tenantSelection/TenantSelection')
)
const TermsAndConditions = lazy(
  () => import('@pages/legal/TermsAndConditions/TermsAndConditions')
)
const PrivacyPolicy = lazy(
  () => import('@pages/legal/PrivacyPolicy/PrivacyPolicy')
)

const ChangePassword = lazy(() => import('@pages/security/ChangePassword'))
const ChangePasswordSuccess = lazy(
  () => import('@pages/security/ChangePasswordSuccess')
)
const Security = lazy(() => import('@pages/security/Security'))
const DeleteAccount = lazy(() => import('@pages/security/DeleteAccount'))

const authRoutes = [
  // Authenticated root - Tenant selection (landing page after login)
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AuthLayout>
          <Suspense fallback={null}>
            <TenantSelection />
          </Suspense>
        </AuthLayout>
      </ProtectedRoute>
    ),
  },
  // Redirects for old signin/signup routes
  { path: PAGE_URL.SIGN_IN, element: <Navigate to='/auth' replace /> },
  { path: PAGE_URL.SIGN_UP, element: <Navigate to='/auth' replace /> },

  // DEV: Test route for delete account UI (no auth required)
  {
    path: '/test-delete-account',
    element: (
      <AuthLayout>
        <Suspense fallback={null}>
          <DeleteAccount />
        </Suspense>
      </AuthLayout>
    ),
  },
  // Unauthenticated routes under /auth
  {
    path: '/auth',
    element: (
      <AuthLayout>
        <Outlet />
      </AuthLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <Auth />
          </Suspense>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <Suspense fallback={null}>
            <Auth state='forgotPassword' />
          </Suspense>
        ),
      },
    ],
  },
  // Protected account security routes
  {
    path: '/security',
    element: (
      <ProtectedRoute>
        <AuthLayout>
          <Outlet />
        </AuthLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <Security />
          </Suspense>
        ),
      },
      {
        path: 'change-password',
        element: (
          <Suspense fallback={null}>
            <ChangePassword />
          </Suspense>
        ),
      },
      {
        path: 'change-password-success',
        element: (
          <Suspense fallback={null}>
            <ChangePasswordSuccess />
          </Suspense>
        ),
      },
      {
        path: 'delete-account',
        element: (
          <Suspense fallback={null}>
            <DeleteAccount />
          </Suspense>
        ),
      },
    ],
  },
  // Public policy pages (no authentication required)
  {
    path: PAGE_URL.TERMS_AND_CONDITIONS,
    element: (
      <Suspense fallback={null}>
        <TermsAndConditions />
      </Suspense>
    ),
  },
  {
    path: PAGE_URL.PRIVACY_POLICY,
    element: (
      <Suspense fallback={null}>
        <PrivacyPolicy />
      </Suspense>
    ),
  },
  // Catch-all: redirect unauthenticated users to /auth
  { path: '*', element: <Navigate to='/auth' replace /> },
]

const AuthRoutesConfig: React.FC = () => {
  const routing = useRoutes(authRoutes)

  return (
    <AuthFormProvider>
      <RedirectValidationGate>{routing}</RedirectValidationGate>
    </AuthFormProvider>
  )
}

export default AuthRoutesConfig
